import React from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Row, Space } from "@masterteam/mt-design-react";
import { object, string, bool, array, number } from "yup";

import api from "../../../providers/api";
import { WizardStep, Field } from "../Wizard";

import { UnitOfMeasureList, findObjBYSource, addToast } from "../../../helpers";

export const detailsValidationSchema = (isOrderValid, isCodeValid) =>
  object().shape({
    ArabicName: string().required(" "),
    EnglishName: string().required(" "),
    ArabicDescription: string().required(" "),
    EnglishDescription: string().required(" "),
    ArabicEquation: string().required(" "),
    EnglishEquation: string().required(" "),
    Source: string(),
    Code: string()
      .required(" ")
      .test(() => isCodeValid),
    Order: number()
      .required(" ")
      .min(1)
      .test(() => isOrderValid),
    HasNoTarget: bool(),
    HasNoBaseline: bool(),
    OwnerModel: object().required(" ").nullable(),
    ObjectiveOwnerModel: object().required(" ").nullable(),
    UnitOfMeasure: object().required(" ").nullable(),
    CalculationMethod: object().required(" ").nullable(),
    EnglishUnitDetails: string().when("UnitOfMeasure", {
      is: (selectedUnit) => selectedUnit.value === "Value",
      then: (schema) => schema.required(" ")
    }),
    ArabicUnitDetails: string().when("UnitOfMeasure", {
      is: (selectedUnit) => selectedUnit.value === "Value",
      then: (schema) => schema.required(" ")
    }),
    Polarity: object().required(" ").nullable(),
    KPIType: object().required(" ").nullable(),
    StrategyType: object().required(" "),
    Strategy: object().nullable().required(" "),
    KPISectors: array().of(object()).min(1, " "),
    Theme: object()
      .nullable()
      .when("StrategyType", {
        is: (StrategyType) => +StrategyType?.Value === 1,
        then: (schema) => schema.required(" ")
      }),
    Pillar: object()
      .nullable()
      .when("StrategyType", {
        is: (StrategyType) => +StrategyType?.Value === 2,
        then: (schema) => schema.required(" ")
      }),
    Level: object()
      .nullable()
      .when("StrategyType", {
        is: (StrategyType) => +StrategyType?.Value === 1,
        then: (schema) => schema.required(" ")
      }),
    Objective: object().required(" ").nullable()
  });

function Details({
  lists,
  formBag,
  isEditMode,
  isReadOnly,
  setHasParameter,
  setHasNoTarget,
  setHasNoBaseline,
  setIsOrderValid,
  setIsCodeValid,
  details,
  state
}) {
  const intl = useIntl();
  const [
    locale,
    polarities,
    calculationMethods,
    strategyTypes,
    objectiveLevels,
    kpiSources
  ] = useSelector(({ language, theme }) => [
    language.locale,
    theme.Polarities.item.Items,
    theme.CalculationMethods.item.Items,
    theme.StrategyTypes.item.Items,
    theme.ObjectiveLevel.item.Items,
    theme.KPISource.item.Items.filter((_item) => _item.Value !== "auto"),
    []
  ]);

  const { values, setFieldValue } = formBag;

  const isAr = locale === "ar";

  function getList(values) {
    const isStrategic = values?.Strategy?.StrategyType === 1;

    if (isStrategic)
      return (
        lists?.strategiesWithThemes
          ?.find((el) => values?.Strategy?.ID === el?.ID)
          ?.Themes?.find((el) => el?.ID === values?.Theme?.ID)
          ?.Objectives?.filter(
            (e) => e?.Level?.toString() === values?.Level?.Value?.toString()
          ) ?? []
      );

    return (
      lists?.strategiesWithPillars
        ?.find((el) => values?.Strategy?.ID === el?.ID)
        ?.Pillars?.find((el) => el?.ID === values?.Pillar?.ID)?.Objectives ?? []
    );
  }

  const checkIsOrderValid = async () => {
    try {
      const res = await api.kpi.checkIsOrderValid(
        values?.Order,
        values?.Strategy?.ID,
        isEditMode
          ? { KPIID: details?.ID, Level: values?.Level?.Value ?? null }
          : { Level: values?.Level?.Value ?? null }
      );
      setIsOrderValid(res.Data);
    } catch (err) {
      addToast.error(err.message, {
        toastId: "get-users-error"
      });
    }
  };

  const checkIsCodeValid = async () => {
    try {
      const res = await api.kpi.checkIsCodeValid(
        values?.Strategy?.ID,
        isEditMode
          ? {
              Code: values?.Code,
              KPIID: details?.ID,
              Level: values?.Level?.Value ?? null
            }
          : { Code: values?.Code, Level: values?.Level?.Value ?? null }
      );
      setIsCodeValid(res.Data);
    } catch (err) {
      addToast.error(err.message, {
        toastId: "get-users-error"
      });
    }
  };

  return (
    <WizardStep name="KPI Details">
      <Space direction="vertical" size="medium" className="details-container">
        <Row size="small">
          <Field
            name="ArabicName"
            label={intl.formatMessage({ id: "kpi.kpi-arabic-name" })}
            inputProps={{
              placeholder: intl.formatMessage({ id: "kpi.kpi-arabic-name" })
            }}
            disabled={isReadOnly}
            hideErrorMessage
            required
          />

          <Field
            name="EnglishName"
            label={intl.formatMessage({ id: "kpi.kpi-english-name" })}
            inputProps={{
              placeholder: intl.formatMessage({ id: "kpi.kpi-english-name" })
            }}
            disabled={isReadOnly}
            hideErrorMessage
            required
          />
        </Row>

        <Row size="small">
          <Field
            type="textarea"
            name="ArabicDescription"
            label={intl.formatMessage({ id: "kpi.kpi-arabic-description" })}
            inputProps={{
              placeholder: intl.formatMessage({
                id: "kpi.kpi-arabic-description"
              })
            }}
            disabled={isReadOnly}
            hideErrorMessage
            required
          />

          <Field
            type="textarea"
            name="EnglishDescription"
            label={intl.formatMessage({ id: "kpi.kpi-english-description" })}
            inputProps={{
              placeholder: intl.formatMessage({
                id: "kpi.kpi-english-description"
              })
            }}
            disabled={isReadOnly}
            hideErrorMessage
            required
          />
        </Row>

        <Row size="small">
          <Field
            type="textarea"
            name="ArabicEquation"
            label={intl.formatMessage({ id: "kpi.kpi-arabic-formula" })}
            inputProps={{
              placeholder: intl.formatMessage({ id: "kpi.kpi-arabic-formula" })
            }}
            disabled={isReadOnly}
            hideErrorMessage
            required
          />

          <Field
            type="textarea"
            name="EnglishEquation"
            label={intl.formatMessage({ id: "kpi.kpi-english-formula" })}
            inputProps={{
              placeholder: intl.formatMessage({
                id: "kpi.kpi-english-formula"
              })
            }}
            disabled={isReadOnly}
            hideErrorMessage
            required
          />
        </Row>

        <div className="line" />

        <Row size="small">
          <Field
            type="select"
            label={intl.formatMessage({ id: "kpi-info.kpi-owner" })}
            name="OwnerModel"
            inputProps={{
              options: lists.users,
              getOptionLabel: (el) => el.DisplayName,
              getOptionValue: (el) => el?.UserName,
              menuPlacement: "top",
              isLoading: lists.loading.users,
              placeholder: intl.formatMessage({ id: "kpi-info.kpi-owner" })
            }}
            disabled={isReadOnly}
            hideErrorMessage
            required
          />

          <Field
            type="select"
            name="ObjectiveOwnerModel"
            label={intl.formatMessage({ id: "kpi-info.objective-owner" })}
            inputProps={{
              options: lists.users,
              getOptionLabel: (el) => el.DisplayName,
              getOptionValue: (el) => el?.UserName,
              menuPlacement: "top",
              isLoading: lists.loading.users,
              placeholder: intl.formatMessage({
                id: "kpi-info.objective-owner"
              })
            }}
            disabled={isReadOnly}
            hideErrorMessage
            required
          />

          <Field
            type="select"
            label={intl.formatMessage({ id: "kpi-info.kpi-type" })}
            name="KPIType"
            disabled={isEditMode || isReadOnly}
            inputProps={{
              options: lists.kpiTypes,
              menuPlacement: "top",
              getOptionLabel: (el) => (isAr ? el?.ArabicName : el?.EnglishName),
              getOptionValue: (el) => el?.KPITypeID,
              isLoading: lists.loading.kpiTypes,
              placeholder: intl.formatMessage({
                id: "kpi-info.kpi-type"
              })
            }}
            hideErrorMessage
            required
          />
        </Row>

        <Row size="small">
          <Field
            type="select"
            name="StrategyType"
            label={intl.formatMessage({ id: "strategy.strategy-type" })}
            inputProps={{
              options: strategyTypes,
              getOptionLabel: (el) => (isAr ? el?.Arabic : el.English),
              getOptionValue: (el) => el?.Value,
              menuPlacement: "top",
              placeholder: intl.formatMessage({ id: "strategy.strategy-type" }),
              onChange: (e) => {
                setFieldValue("StrategyType", e);
                setFieldValue("Strategy", null);
                setFieldValue("Theme", null);
                setFieldValue("Pillar", null);
                setFieldValue("Objective", null);
                setFieldValue("Level", null);
              }
            }}
            disabled={isReadOnly || isEditMode}
            hideErrorMessage
            required
          />
          <Field
            type="select"
            name="Strategy"
            label={intl.formatMessage({ id: "common.strategy" })}
            inputProps={{
              options:
                +values?.StrategyType?.Value === 1
                  ? lists?.strategiesWithThemes
                  : lists?.strategiesWithPillars,

              getOptionLabel: (el) => (isAr ? el?.ArabicName : el.EnglishName),
              getOptionValue: (el) => el?.ID,
              menuPlacement: "top",
              isLoading: lists.loading.strategiesWithThemes,
              placeholder: intl.formatMessage({ id: "common.strategy" }),
              onChange: (e) => {
                setFieldValue("Strategy", e);
                setFieldValue("Theme", null);
                setFieldValue("Pillar", null);
                setFieldValue("Objective", null);
              }
            }}
            disabled={isReadOnly || isEditMode}
            hideErrorMessage
            required
          />

          {+values?.StrategyType?.Value === 1 ? (
            <Field
              type="select"
              name="Theme"
              label={intl.formatMessage({ id: "common.theme" })}
              inputProps={{
                options: values?.Strategy?.Themes || [],
                getOptionLabel: (el) =>
                  isAr ? el?.ArabicName : el.EnglishName,
                getOptionValue: (el) => el?.ID,
                menuPlacement: "top",
                placeholder: intl.formatMessage({ id: "common.theme" }),
                onChange: (e) => {
                  setFieldValue("Theme", e);
                  setFieldValue("Objective", null);
                }
              }}
              disabled={isReadOnly}
              hideErrorMessage
              required
            />
          ) : (
            <Field
              type="select"
              name="Pillar"
              label={intl.formatMessage({ id: "common.pillar" })}
              inputProps={{
                options: values?.Strategy?.Pillars || [],
                getOptionLabel: (el) =>
                  isAr ? el?.ArabicName : el.EnglishName,
                getOptionValue: (el) => el?.ID,
                menuPlacement: "top",
                placeholder: intl.formatMessage({ id: "common.pillar" }),
                onChange: (e) => {
                  setFieldValue("Pillar", e);
                  setFieldValue("Objective", null);
                }
              }}
              disabled={isReadOnly}
              hideErrorMessage
              required
            />
          )}
        </Row>

        <Row size="small">
          <Field
            type="select"
            name="Level"
            label={intl.formatMessage({ id: "common.level" })}
            inputProps={{
              options: objectiveLevels,
              getOptionLabel: (el) => (isAr ? el?.Arabic : el.English),
              getOptionValue: (el) => el?.Value,
              menuPlacement: "top",
              placeholder: intl.formatMessage({ id: "common.level" }),
              onChange: (e) => {
                setFieldValue("Level", e);
                setFieldValue("Objective", null);
              }
            }}
            disabled={isReadOnly || +values?.StrategyType?.Value === 2}
            hideErrorMessage
            required
          />
          <Field
            type="select"
            name="Objective"
            label={intl.formatMessage({
              id: "common.strategic-objective"
            })}
            inputProps={{
              options: getList(values),
              // (values?.Theme
              //   ? values?.Theme?.Objectives?.filter(
              //       (e) => +e.Level === +values?.Level?.Value
              //     )
              //   : values?.Pillar
              //   ? values?.Pillar?.Objectives
              //   : []) || [],
              getOptionLabel: (el) => (isAr ? el?.ArabicName : el.EnglishName),
              getOptionValue: (el) => el?.ID,
              menuPlacement: "top",
              placeholder: intl.formatMessage({
                id: "common.strategic-objective"
              }),
              onChange: (e) => {
                setFieldValue("Objective", e);
              }
            }}
            disabled={isReadOnly}
            hideErrorMessage
            required
          />
          <Field
            type="select"
            label={intl.formatMessage({
              id: "kpi-info.calculation-method-and-end-of-year"
            })}
            name="CalculationMethod"
            inputProps={{
              options: calculationMethods,
              getOptionLabel: (el) => (isAr ? el?.Arabic : el?.English),
              getOptionValue: (el) => el?.Value,
              menuPlacement: "top",
              placeholder: intl.formatMessage({
                id: "kpi-info.calculation-method-and-end-of-year"
              }),
              onChange: (val) => {
                setFieldValue("CalculationMethod", val);
              }
            }}
            disabled={isReadOnly || isEditMode}
            hideErrorMessage
            required
          />
        </Row>

        <Row size="small">
          <Field
            type="select"
            label={intl.formatMessage({ id: "kpi.unit-of-measure" })}
            name="UnitOfMeasure"
            disabled={isEditMode || isReadOnly}
            inputProps={{
              options: UnitOfMeasureList,
              getOptionLabel: (el) => (isAr ? el.labelAr : el.label),
              getOptionValue: (el) => el?.value,
              menuPlacement: "top",
              placeholder: intl.formatMessage({ id: "kpi.unit-of-measure" })
            }}
            hideErrorMessage
            required
          />
          {values.UnitOfMeasure?.value === "Value" ? (
            <>
              <Field
                name="EnglishUnitDetails"
                disabled={isEditMode || isReadOnly}
                label={intl.formatMessage({ id: "kpi.english-unit-type" })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "kpi.english-unit-type"
                  })
                }}
                hideErrorMessage
                required
              />
              <Field
                name="ArabicUnitDetails"
                disabled={isEditMode || isReadOnly}
                label={intl.formatMessage({ id: "kpi.arabic-unit-type" })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "kpi.arabic-unit-type"
                  })
                }}
                hideErrorMessage
                required
              />
            </>
          ) : null}
        </Row>

        <Row size="small">
          <Field
            name="Order"
            type="number"
            label={intl.formatMessage({ id: "common.order" })}
            inputProps={{
              min: 0,
              step: "any",
              onKeyDown: (evt) => {
                if (evt.key === "e" || evt.key === "-" || evt.key === "+") {
                  evt.preventDefault();
                }
              },
              placeholder: intl.formatMessage({
                id: "common.order"
              }),
              onBlur: checkIsOrderValid
            }}
            disabled={isReadOnly}
            hideErrorMessage
            required
          />

          <Field
            name="Code"
            label={intl.formatMessage({ id: "common.code" })}
            inputProps={{
              placeholder: intl.formatMessage({
                id: "common.code"
              }),
              onBlur: checkIsCodeValid
            }}
            disabled={isReadOnly}
            hideErrorMessage
            required
          />
        </Row>

        <Row size="small">
          <Field
            type="select"
            label={intl.formatMessage({ id: "kpi-info.polarity" })}
            name="Polarity"
            disabled={isEditMode || isReadOnly}
            inputProps={{
              options: polarities,
              getOptionLabel: (el) => (isAr ? el?.Arabic : el?.English),
              getOptionValue: (el) => el?.Value,
              menuPlacement: "top",
              placeholder: intl.formatMessage({
                id: "kpi-info.polarity"
              })
            }}
            hideErrorMessage
            required
          />

          <Field
            type="select"
            name="KPISectors"
            label={intl.formatMessage({
              id: "common.sector"
            })}
            inputProps={{
              options: lists.sectors || [],
              getOptionLabel: (el) => (isAr ? el?.ArabicName : el.EnglishName),
              getOptionValue: (el) => el?.ID,
              menuPlacement: "top",
              isMulti: true,
              placeholder: intl.formatMessage({
                id: "common.sector"
              }),
              onChange: (e) => {
                setFieldValue("KPISectors", e);
              }
            }}
            disabled={isReadOnly}
            hideErrorMessage
            required
          />

          <Field
            name="Source"
            label={intl.formatMessage({ id: "kpi-info.kpi-source" })}
            inputProps={{
              placeholder: intl.formatMessage({
                id: "kpi-info.kpi-source"
              })
            }}
            disabled={isReadOnly}
            hideErrorMessage
          />
        </Row>

        <Row size="small">
          <Field
            name="HasNoTarget"
            type="checkbox"
            label={intl.formatMessage({
              id: "kpi-info.no-need-target"
            })}
            inputProps={{
              highContrast: true,
              onChange: (val) => {
                if (values.HasNoBaseline) setFieldValue("HasNoBaseline", false);

                setFieldValue("HasNoTarget", !values.HasNoTarget);
                setHasNoTarget(!values.HasNoTarget);
                if (!values.HasNoTarget) {
                  setFieldValue(
                    "DataSource",
                    findObjBYSource(kpiSources, "manual")
                  );
                  setFieldValue("Parameters", [
                    {
                      ParameterName: "",
                      Description: ""
                    }
                  ]);
                  setFieldValue("Formula", []);
                  setFieldValue("Baseline", null);
                  setFieldValue(
                    "KPIMeasures",
                    values.KPIMeasures?.map((e) => {
                      return { ...e, Target: null };
                    })
                  );
                  setHasParameter(false);
                }
              },
              placeholder: intl.formatMessage({
                id: "kpi-info.no-need-target"
              })
            }}
            hideLabel
            disabled={isReadOnly || (isEditMode && state?.DisableCheckBoxes)}
            hideErrorMessage
          />

          <Field
            name="HasNoBaseline"
            type="checkbox"
            label={intl.formatMessage({
              id: "kpi-info.no-need-baseline"
            })}
            inputProps={{
              highContrast: true,
              onChange: (val) => {
                if (values.HasNoTarget) setFieldValue("HasNoTarget", false);

                setFieldValue("HasNoBaseline", !values.HasNoBaseline);
                setHasNoBaseline(!values.HasNoBaseline);
                if (!values.HasNoBaseline) {
                  setFieldValue(
                    "DataSource",
                    findObjBYSource(kpiSources, "manual")
                  );
                  setFieldValue("Parameters", [
                    {
                      ParameterName: "",
                      Description: ""
                    }
                  ]);
                  setFieldValue("Formula", []);
                  setFieldValue("Baseline", null);
                  setFieldValue(
                    "KPIMeasures",
                    values.KPIMeasures?.map((e) => {
                      return { ...e, Target: null };
                    })
                  );
                  setHasParameter(false);
                }
              },
              placeholder: intl.formatMessage({
                id: "kpi-info.no-need-baseline"
              })
            }}
            hideLabel
            disabled={isReadOnly || (isEditMode && state?.DisableCheckBoxes)}
            hideErrorMessage
          />
        </Row>
      </Space>
    </WizardStep>
  );
}

export default Details;
